import React, {
  memo, useEffect, useReducer, useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { updateMasterIbAgreement } from "store/actions";

import {
  Row, Col, Button,
  Modal, ModalBody, ModalHeader,
  Input,
  Spinner,
  Alert
} from "reactstrap";

import validatePositiveInputs from "helpers/validatePositiveInputs";
import AvFieldSelect from "components/Common/AvFieldSelect";
import { getProductCategoryLabel } from "common/productCategories";

const getAccountTypeName = (accountTypeId, accountTypes) => {
  return accountTypes?.find((type) => type._id === accountTypeId)?.title;
};

const getAccountType = (accountTypeId, accountTypes) => {
  return accountTypes?.find((type) => type._id === accountTypeId);
};

const toSafeNumber = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return 0;
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const buildInitialValues = (customerAgreementValues, accountTypes, products) => (customerAgreementValues || []).map((agr) => {
  const accountType = getAccountType(agr.accountTypeId, accountTypes);
  const isInvestor = accountType?.type === "INVESTOR";
  if (isInvestor) {
    return {
      accountTypeId: agr.accountTypeId,
      pamProfitShare: agr.pamProfitShare ?? 0,
    };
  }
  return {
    accountTypeId: agr.accountTypeId,
    markup: agr.markup ?? "",
    products: (products || []).reduce((acc, prod) => {
      acc[prod] = {
        rebate: agr?.products?.[prod]?.rebate ?? 0,
        commission: agr?.products?.[prod]?.commission ?? 0,
      };
      return acc;
    }, {}),
  };
});

const EditMasterIbModal = ({ show, toggle, accountTypes: allAccountTypes, customerId, products, agreement, markups }) => {
  const customerAgreement = agreement.members && agreement.members.find((mem) => mem.customerId._id === customerId);
  const { clientDetails } = useSelector((state) => state?.clientReducer);
  const [accountTypes, setAccountTypes] = useState([]);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { submitting, error: submitError, clearingCounter } = useSelector((state) => state.ibAgreements);

  const [payload, dispatcher] = useReducer((state, action) => {
    switch (action.type) {
      case "UPDATE_TITLE":
        return {
          ...state,
          title: action.payload 
        };
      case "INIT_VALUES":
        return {
          ...state,
          values: action.payload 
        };
      case "UPDATE_PAMM_PROFIT_SHARE":
        return {
          ...state,
          values: state.values.map((val) => val.accountTypeId === action.payload.accountTypeId
            ? {
              ...val,
              pamProfitShare: action.payload.pamProfitShare 
            }
            : val),
        };
      case "UPDATE_PRODUCT_REBATE":
        return {
          ...state,
          values: state.values.map((val) => {
            if (val.accountTypeId !== action.payload.accountTypeId) return val;
            return {
              ...val,
              products: {
                ...val.products,
                [action.payload.product]: {
                  ...val.products?.[action.payload.product],
                  rebate: action.payload.rebate,
                },
              },
            };
          }),
        };
      case "UPDATE_PRODUCT_COMMISSION":
        return {
          ...state,
          values: state.values.map((val) => {
            if (val.accountTypeId !== action.payload.accountTypeId) return val;
            return {
              ...val,
              products: {
                ...val.products,
                [action.payload.product]: {
                  ...val.products?.[action.payload.product],
                  commission: action.payload.commission,
                },
              },
            };
          }),
        };
      case "UPDATE_MARKUP":
        return {
          ...state,
          values: state.values.map((val) => val.accountTypeId === action.payload.accountTypeId
            ? {
              ...val,
              markup: action.payload.markup 
            }
            : val),
        };
      default:
        return state;
    }
  }, {
    title: agreement.title ?? "",
    values: [] 
  });

  useEffect(() => {
    if (clearingCounter > 0 && show) {
      toggle();
    }
  }, [clearingCounter]);

  useEffect(() => {
    if (show && allAccountTypes && allAccountTypes.length > 0) {
      const newAccountTypes = new Set();
      allAccountTypes.forEach((acc) => {
        if (acc.type === "INVESTOR" && clientDetails?.email?.endsWith("mailinator.com")) {
          newAccountTypes.add(acc);
        } else if (acc.type !== "INVESTOR") {
          newAccountTypes.add(acc);
        }
      });
      setAccountTypes([...Array.from(newAccountTypes)]);
    }
  }, [allAccountTypes, show, clientDetails]);

  useEffect(() => {
    if (show && customerAgreement) {
      dispatcher({
        type: "INIT_VALUES",
        payload: buildInitialValues(customerAgreement.values, accountTypes, products),
      });
    }
  }, [show, customerAgreement, accountTypes]);

  if (!show) return null;

  const { title, values } = payload;

  return (
    <Modal isOpen={show} toggle={toggle} centered={true} scrollable={true}>
      <ModalHeader toggle={toggle} tag="h4">
        {t("Edit Master IB Agreement")}
      </ModalHeader>
      <ModalBody >
        <AvForm
          onValidSubmit={() => {
            const numericValues = values.map((val) => ({
              ...val,
              pamProfitShare: val.pamProfitShare !== undefined
                ? toSafeNumber(val.pamProfitShare) : undefined,
              products: val.products
                ? Object.fromEntries(
                  Object.entries(val.products).map(([prod, pv]) => [
                    prod,
                    {
                      rebate: toSafeNumber(pv.rebate),
                      commission: toSafeNumber(pv.commission),
                    },
                  ])
                )
                : val.products,
            }));
            dispatch(updateMasterIbAgreement({
              id: agreement._id,
              customerId,
              title,
              values: numericValues,
            }));
          }}
        >
          <AvField
            name={"title"}
            label={t("Agreement name")}
            className="mb-3"
            type="text"
            value={title}
            errorMessage={t("Required!")}
            validate={{ required: { value: true } }}
            onChange={(e) => {
              dispatcher({
                type: "UPDATE_TITLE",
                payload: e.target.value,
              });
            }}
          />
          {customerAgreement?.values.map((agr, agrIdx) => {
            const value = values?.find((val) => val.accountTypeId === agr.accountTypeId);
            return getAccountType(agr.accountTypeId, accountTypes)?.type === "INVESTOR" ? <>
              <Row key={agr._id}>
                <h6>{getAccountTypeName(agr.accountTypeId, accountTypes)}</h6>
                <Row className="justify-content-start text-center">
                  <Col md="12">{t("PAMM Profit Share")}</Col>
                </Row>
                <Row>
                  <Col md="12">
                    <Input
                      type="text"
                      value={value?.pamProfitShare}
                      onKeyPress={(e) => validatePositiveInputs(e)}
                      onChange={(e) => {
                        dispatcher({
                          type: "UPDATE_PAMM_PROFIT_SHARE",
                          payload: {
                            accountTypeId: agr.accountTypeId,
                            pamProfitShare: e.target.value,
                          },
                        });
                      }}
                    />
                  </Col>
                </Row>
              </Row>
            </> :
              <Row key={agr._id}>
                <h6>{getAccountTypeName(agr.accountTypeId, accountTypes)}</h6>
                <Row className="justify-content-start">
                  <Col md="3">{t("Type")}</Col>
                  <Col>{t("Total Rebate")}</Col>
                  <Col>{t("Total Commission")}</Col>
                </Row>
                {products.map((prod, prodIdx) =>
                  <>
                    <Row key={prodIdx} className="my-1 align-items-center">
                      <Col md="2">{getProductCategoryLabel(prod)}</Col>
                      <Col>
                        <Input
                          type="text"
                          value={value?.products?.[prod]?.rebate === "" ? 0 : (value?.products?.[prod]?.rebate ?? 0)}
                          onKeyPress={(e) => validatePositiveInputs(e)}
                          onChange={(e) => {
                            dispatcher({
                              type: "UPDATE_PRODUCT_REBATE",
                              payload: {
                                accountTypeId: agr.accountTypeId,
                                product: prod,
                                rebate: e.target.value,
                              },
                            });
                          }}
                        />
                      </Col>
                      <Col>
                        <Input
                          className="mt-3"
                          type="text"
                          value={value?.products?.[prod]?.commission === "" ? 0 : (value?.products?.[prod]?.commission ?? 0)}
                          onKeyPress={(e) => validatePositiveInputs(e)}
                          onChange={(e) => {
                            dispatcher({
                              type: "UPDATE_PRODUCT_COMMISSION",
                              payload: {
                                accountTypeId: agr.accountTypeId,
                                product: prod,
                                commission: e.target.value,
                              },
                            });
                          }}
                        />
                      </Col>
                    </Row>
                  </>
                )}
                <Row key={agrIdx} className="my-1 align-items-center">
                  <Col md="2">
                    {t("Markup")}
                  </Col>
                  <Col>
                    <AvFieldSelect
                      name={`values[${agrIdx}].markup`}
                      value={value?.markup}
                      onChange={(markup) => {
                        dispatcher({
                          type: "UPDATE_MARKUP",
                          payload: {
                            accountTypeId: agr.accountTypeId,
                            markup,
                          },
                        });
                      }}
                      options={(markups || []).map((obj)=>{
                        return ({
                          label: `${obj}`,
                          value: obj
                        });
                      })}
                    />
                  </Col>
                </Row>
                <hr className="my-3" />
              </Row>;
          })}
          {!submitting && submitError && (
            <Alert color="danger" className="mt-3">
              {submitError}
            </Alert>
          )}
          <div className="d-flex align-items-center gap-2 mt-3">
            <Button type="submit" disabled={submitting}>
              {t("Submit")}
            </Button>
            {submitting && (
              <span>
                <Spinner size="sm" color="primary" /> {t("Submitting...")}
              </span>
            )}
          </div>
        </AvForm>
      </ModalBody>
    </Modal >);
};

export default memo(EditMasterIbModal);
